/**
 * AWS Amplify Configuration
 *
 * Configures Amplify Auth with Cognito settings from environment variables.
 * Always uses Cognito Hosted UI (SSO) for authentication.
 */

import { Amplify, ResourcesConfig } from 'aws-amplify';
import { amplifyLogger } from './logger';

// =============================================================================
// Configuration from Environment
// =============================================================================

const cognitoConfig = {
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  domain: import.meta.env.VITE_COGNITO_DOMAIN || '',
  region: import.meta.env.VITE_AWS_REGION || 'eu-west-1',
  redirectSignIn: import.meta.env.VITE_REDIRECT_URI || window.location.origin,
  redirectSignOut: import.meta.env.VITE_REDIRECT_URI || window.location.origin,
};

// =============================================================================
// Check if Cognito is configured
// =============================================================================

export const isCognitoConfigured = (): boolean => {
  return !!(cognitoConfig.userPoolId && cognitoConfig.userPoolClientId);
};

export const isOAuthConfigured = (): boolean => {
  return isCognitoConfigured() && !!cognitoConfig.domain;
};

// =============================================================================
// Initialize Amplify
// =============================================================================

export function configureAmplify(): void {
  if (!isCognitoConfigured()) {
    amplifyLogger.warn(
      'Cognito not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID environment variables.'
    );
    return;
  }

  // Debug logging
  console.log('Cognito Configuration:', {
    userPoolId: cognitoConfig.userPoolId,
    clientId: cognitoConfig.userPoolClientId,
    domain: cognitoConfig.domain,
    redirectSignIn: cognitoConfig.redirectSignIn,
    redirectSignOut: cognitoConfig.redirectSignOut,
    origin: window.location.origin
  });

  const authConfig: ResourcesConfig['Auth'] = {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.userPoolClientId,
      ...(cognitoConfig.domain && {
        loginWith: {
          oauth: {
            domain: cognitoConfig.domain,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [cognitoConfig.redirectSignIn],
            redirectSignOut: [cognitoConfig.redirectSignOut],
            responseType: 'code',
          },
        },
      }),
    },
  };

  Amplify.configure({ Auth: authConfig });

  amplifyLogger.log('Configured with Cognito User Pool:', cognitoConfig.userPoolId);
  if (!cognitoConfig.domain) {
    amplifyLogger.warn('VITE_COGNITO_DOMAIN not set — OAuth/Hosted UI login will be unavailable');
  }
}

// =============================================================================
// Export configuration for debugging
// =============================================================================

export const getAmplifyConfig = () => ({
  userPoolId: cognitoConfig.userPoolId,
  clientId: cognitoConfig.userPoolClientId,
  domain: cognitoConfig.domain,
  region: cognitoConfig.region,
  redirectUri: cognitoConfig.redirectSignIn,
  isConfigured: isCognitoConfigured(),
});
